import { useTranslation } from 'react-i18next';
import { Box, Button, Modal, Text, useKeyDown } from '@sendible/design-system';
import { useDeleteMultipleMessages } from '@sendible/frontendv2/src/models/calendar';
import { DeleteActions } from './index.styles';
import { useSelectionContext } from '../../selectionContext';

interface DeleteMultipleMessagesModalProps {
  close: () => void;
}

export const DeleteMultipleMessagesModal = ({ close }: DeleteMultipleMessagesModalProps) => {
  const { t } = useTranslation('calendar');
  const { deselectAll, removeFromSelection, selectedMessageIds } = useSelectionContext();
  const deleteMultipleMessages = useDeleteMultipleMessages();

  useKeyDown(close, 'Escape');

  const handleConfirm = () => {
    deleteMultipleMessages.mutate(selectedMessageIds, {
      onSuccess: ({ succeededIds, failedIds }) => {
        removeFromSelection(succeededIds);

        // On partial failure, leave the modal open over the still-selected failed ids (the
        // toast from useDeleteMultipleMessages already explains what happened) instead of
        // silently closing as if everything had gone through.
        if (failedIds.length === 0) {
          deselectAll();
          close();
        }
      },
    });
  };

  const titleKey = selectedMessageIds.length > 1 ? 'delete_confirm_bulk_title_other' : 'delete_confirm_bulk_title_one';
  const textKey = selectedMessageIds.length > 1 ? 'delete_confirm_bulk_text_other' : 'delete_confirm_bulk_text_one';

  return (
    <Modal
      close={close}
      customClass="global-modal"
      headingTitle={t(titleKey, { count: selectedMessageIds.length }) as string}
      testId="delete-multiple-messages-modal"
      variation="small"
      ariaCloseLabel={t('close') as string}
    >
      <Box
        orientation="column"
        flex={1}
      >
        <Text>{t(textKey, { count: selectedMessageIds.length })}</Text>
        <DeleteActions contentJustify="flex-end">
          <Button
            onClick={close}
            label={t('delete_confirm_cancel') as string}
            size={16}
            data-testid="delete-multiple-messages-modal-cancel"
            variation="outline"
            autoFocus
          />
          <Button
            appearance="primary"
            onClick={handleConfirm}
            label={t('delete_confirm_yes') as string}
            isLoading={deleteMultipleMessages.isPending}
            size={16}
            data-testid="delete-multiple-messages-modal-confirm"
            variation="fill"
          />
        </DeleteActions>
      </Box>
    </Modal>
  );
};
